import { useCallback } from 'react';

import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { useCanvas } from '../../../../core/components/canvas/hooks/useCanvas.js';
import { Point } from '../../../../core/types/point.js';
import { getHexagonGeometry } from '../../../utils/get-hexagon-geometry.js';
import { drawLabelAndValue } from '../utils/draw-label-and-value.js';
import { fillShape } from '../utils/fill-shape.js';
import { removeTransparentBorder } from '../utils/remove-transparent-border.js';

interface CanvasHexagonRendererProps {
  x: number;
  y: number;
  width: number;
  fillColor: string;
  value: string | number;
  name?: string;
  strokeColor?: 'transparent' | string;
  radius?: number;
  showLabels?: boolean;
  truncationMode?: TruncationMode;
}

const createHexagonPath = (
  ctx: CanvasRenderingContext2D,
  hexagonGeometry: Point[],
  radius: number,
) => {
  ctx.beginPath();
  // Web-kit browsers need to move to the initial point to avoid weird behavior: APPDEV-8494
  ctx.moveTo(hexagonGeometry[0].x, hexagonGeometry[0].y);
  for (let i = 0; i < hexagonGeometry.length; i += 2) {
    const vertex = hexagonGeometry[i];
    const lineBisect = hexagonGeometry[i + 1];

    ctx.arcTo(vertex.x, vertex.y, lineBisect.x, lineBisect.y, radius);
  }
  ctx.closePath();
};

export const CanvasHexagonRenderer = (props: CanvasHexagonRendererProps) => {
  const {
    x,
    y,
    name,
    value,
    width,
    fillColor,
    strokeColor = fillColor,
    radius = 0,
    showLabels,
    truncationMode,
  } = props;
  const { hexagonGeometry, textArea } = getHexagonGeometry(x, y, width);
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      createHexagonPath(ctx, hexagonGeometry, radius);
      removeTransparentBorder(ctx, strokeColor);
      fillShape(ctx, fillColor);
      if (showLabels) {
        drawLabelAndValue(
          ctx,
          value,
          textArea,
          fillColor,
          name,
          truncationMode,
        );
      }

      return false;
    },
    [
      x,
      y,
      width,
      fillColor,
      strokeColor,
      showLabels,
      truncationMode,
      name,
      value,
    ],
  );

  useCanvas('honeycomb-hexagon', draw);

  return null;
};

CanvasHexagonRenderer['displayName'] = 'CanvasHexagonRenderer';
